// 4 modern logo proposals for TERRE ET MER CONCIERGERIE
// Each logo is provided in 2 variants: dark (for light bg) and light (for dark bg).
// Colors: navy #0F3D52 (sage), amber #E08F5C (gold), ink #0B1A26.

// ============================================================
// CONCEPT A, Monogramme T·M dans un cercle + wordmark
// Style : maison de luxe, signature couture
// ============================================================
export const LogoA = ({ variant = "dark", className = "" }) => {
  const ink = variant === "dark" ? "#0B1A26" : "#FFFFFF";
  const accent = "#E08F5C";
  return (
    <svg viewBox="0 0 360 100" className={className} role="img" aria-label="Terre et Mer Conciergerie">
      {/* Circle with T·M monogram */}
      <circle cx="50" cy="50" r="42" fill="none" stroke={ink} strokeWidth="1.5" />
      <circle cx="50" cy="50" r="32" fill="none" stroke={accent} strokeWidth="0.5" />
      <text x="50" y="60" textAnchor="middle" fontFamily="Fraunces, Georgia, serif" fontWeight="400" fontStyle="italic" fontSize="32" fill={ink}>T</text>
      <text x="50" y="60" textAnchor="middle" fontFamily="Fraunces, Georgia, serif" fontWeight="400" fontStyle="italic" fontSize="32" fill={accent} dx="14">m</text>
      <line x1="58" y1="38" x2="62" y2="62" stroke={accent} strokeWidth="0.8" />
      {/* Wordmark */}
      <text x="115" y="48" fontFamily="Fraunces, Georgia, serif" fontWeight="300" fontSize="22" fill={ink} letterSpacing="2">TERRE</text>
      <text x="200" y="48" fontFamily="Fraunces, Georgia, serif" fontWeight="400" fontStyle="italic" fontSize="22" fill={accent}>et</text>
      <text x="232" y="48" fontFamily="Fraunces, Georgia, serif" fontWeight="300" fontSize="22" fill={ink} letterSpacing="2">MER</text>
      <text x="115" y="70" fontFamily="Outfit, sans-serif" fontWeight="500" fontSize="9" fill={ink} opacity="0.65" letterSpacing="4">C O N C I E R G E R I E</text>
    </svg>
  );
};

// ============================================================
// CONCEPT B, Wordmark éditorial pur avec ligne décorative
// Style : magazine, presse haut de gamme
// ============================================================
export const LogoB = ({ variant = "dark", className = "" }) => {
  const ink = variant === "dark" ? "#0B1A26" : "#FFFFFF";
  const accent = "#E08F5C";
  return (
    <svg viewBox="0 0 360 110" className={className} role="img" aria-label="Terre et Mer Conciergerie">
      <text x="180" y="48" textAnchor="middle" fontFamily="Fraunces, Georgia, serif" fontWeight="300" fontSize="38" fill={ink} letterSpacing="3">TERRE</text>
      <text x="180" y="78" textAnchor="middle" fontFamily="Fraunces, Georgia, serif" fontWeight="400" fontStyle="italic" fontSize="28" fill={accent}>et Mer</text>
      {/* Decorative wave line */}
      <path d="M 90 90 Q 130 84, 170 90 T 250 90 T 270 90" fill="none" stroke={accent} strokeWidth="0.8" strokeLinecap="round" />
      <text x="180" y="105" textAnchor="middle" fontFamily="Outfit, sans-serif" fontWeight="600" fontSize="8" fill={ink} opacity="0.7" letterSpacing="6">C O N C I E R G E R I E</text>
    </svg>
  );
};

// ============================================================
// CONCEPT C, Symbole vague stylisée + wordmark
// Style : minimaliste contemporain, écho à la mer
// ============================================================
export const LogoC = ({ variant = "dark", className = "" }) => {
  const ink = variant === "dark" ? "#0B1A26" : "#FFFFFF";
  const accent = "#E08F5C";
  return (
    <svg viewBox="0 0 380 100" className={className} role="img" aria-label="Terre et Mer Conciergerie">
      {/* Stylized wave symbol */}
      <g transform="translate(20, 30)">
        <line x1="0" y1="0" x2="50" y2="0" stroke={ink} strokeWidth="1.5" />
        <path d="M 0 14 Q 12 6, 25 14 T 50 14" fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" />
        <path d="M 0 28 Q 12 22, 25 28 T 50 28" fill="none" stroke={ink} strokeWidth="1" strokeLinecap="round" opacity="0.6" />
      </g>
      {/* Wordmark */}
      <text x="100" y="48" fontFamily="Fraunces, Georgia, serif" fontWeight="300" fontSize="26" fill={ink} letterSpacing="2">TERRE</text>
      <text x="200" y="48" fontFamily="Fraunces, Georgia, serif" fontWeight="400" fontStyle="italic" fontSize="26" fill={accent}>et</text>
      <text x="238" y="48" fontFamily="Fraunces, Georgia, serif" fontWeight="300" fontSize="26" fill={ink} letterSpacing="2">MER</text>
      <text x="100" y="72" fontFamily="Outfit, sans-serif" fontWeight="500" fontSize="9" fill={ink} opacity="0.65" letterSpacing="4">C O N C I E R G E R I E</text>
    </svg>
  );
};

// ============================================================
// CONCEPT D, Cartouche carré + initiales, super contemporain
// Style : startup premium, tech moderne, type Aesop / Studio
// ============================================================
export const LogoD = ({ variant = "dark", className = "" }) => {
  const ink = variant === "dark" ? "#0B1A26" : "#FFFFFF";
  const accent = "#E08F5C";
  const bg = variant === "dark" ? "#0F3D52" : "#FFFFFF";
  return (
    <svg viewBox="0 0 360 100" className={className} role="img" aria-label="Terre et Mer Conciergerie">
      {/* Square cartouche */}
      <rect x="10" y="20" width="60" height="60" rx="4" fill={bg} stroke={ink} strokeWidth="1" />
      <text x="40" y="58" textAnchor="middle" fontFamily="Fraunces, Georgia, serif" fontWeight="300" fontSize="32" fill={variant === "dark" ? "#FFFFFF" : ink} letterSpacing="-1">tm</text>
      <line x1="22" y1="68" x2="58" y2="68" stroke={accent} strokeWidth="1" />
      {/* Wordmark */}
      <text x="90" y="48" fontFamily="Fraunces, Georgia, serif" fontWeight="400" fontSize="22" fill={ink} letterSpacing="1">Terre</text>
      <text x="148" y="48" fontFamily="Fraunces, Georgia, serif" fontWeight="400" fontStyle="italic" fontSize="22" fill={accent}>et</text>
      <text x="178" y="48" fontFamily="Fraunces, Georgia, serif" fontWeight="400" fontSize="22" fill={ink} letterSpacing="1">Mer</text>
      <text x="90" y="70" fontFamily="Outfit, sans-serif" fontWeight="600" fontSize="9" fill={ink} opacity="0.6" letterSpacing="3">CONCIERGERIE · BRETAGNE</text>
    </svg>
  );
};

// ============================================================
// CONCEPT E, Crête héraldique avec couronne de vagues
// Style : maison palace, hôtel particulier 5 étoiles
// ============================================================
export const LogoE = ({ variant = "dark", className = "" }) => {
  const ink = variant === "dark" ? "#0B1A26" : "#FFFFFF";
  const accent = "#E08F5C";
  return (
    <svg viewBox="0 0 360 140" className={className} role="img" aria-label="Terre et Mer Conciergerie">
      {/* Crest with wave crown */}
      <g transform="translate(180, 35)">
        {/* Top ornament: 3 stars */}
        <circle cx="-14" cy="-12" r="1" fill={accent} />
        <circle cx="0" cy="-16" r="1.5" fill={accent} />
        <circle cx="14" cy="-12" r="1" fill={accent} />
        {/* Wave crown */}
        <path d="M -36 -2 Q -24 -10, -12 -2 T 12 -2 T 36 -2" fill="none" stroke={accent} strokeWidth="0.8" strokeLinecap="round" />
        <path d="M -36 4 Q -24 -2, -12 4 T 12 4 T 36 4" fill="none" stroke={ink} strokeWidth="0.6" strokeLinecap="round" opacity="0.6" />
        {/* Center monogram */}
        <text x="0" y="36" textAnchor="middle" fontFamily="Fraunces, Georgia, serif" fontWeight="300" fontStyle="italic" fontSize="34" fill={ink}>T</text>
        <text x="0" y="36" textAnchor="middle" fontFamily="Fraunces, Georgia, serif" fontWeight="300" fontStyle="italic" fontSize="34" fill={accent} dx="14">m</text>
        {/* Lateral lines */}
        <line x1="-50" y1="20" x2="-26" y2="20" stroke={ink} strokeWidth="0.5" opacity="0.4" />
        <line x1="26" y1="20" x2="50" y2="20" stroke={ink} strokeWidth="0.5" opacity="0.4" />
      </g>
      {/* Wordmark */}
      <text x="180" y="100" textAnchor="middle" fontFamily="Fraunces, Georgia, serif" fontWeight="300" fontSize="22" fill={ink} letterSpacing="6">TERRE</text>
      <text x="180" y="100" textAnchor="middle" fontFamily="Fraunces, Georgia, serif" fontWeight="400" fontStyle="italic" fontSize="22" fill={accent} dx="0" dy="0">  </text>
      <text x="180" y="124" textAnchor="middle" fontFamily="Outfit, sans-serif" fontWeight="500" fontSize="8" fill={ink} opacity="0.65" letterSpacing="6">M A I S O N   D E   C O N C I E R G E R I E</text>
      <text x="118" y="100" textAnchor="end" fontFamily="Fraunces, Georgia, serif" fontWeight="300" fontSize="22" fill={ink} letterSpacing="6"></text>
      {/* "et Mer" line below TERRE */}
      <text x="180" y="114" textAnchor="middle" fontFamily="Fraunces, Georgia, serif" fontWeight="300" fontStyle="italic" fontSize="14" fill={accent} letterSpacing="2">et Mer</text>
    </svg>
  );
};

// ============================================================
// CONCEPT F, Monogramme T&M entrelacé Didot (ultra-luxe)
// Style : haute joaillerie, parfum de luxe
// ============================================================
export const LogoF = ({ variant = "dark", className = "" }) => {
  const ink = variant === "dark" ? "#0B1A26" : "#FFFFFF";
  const accent = "#E08F5C";
  return (
    <svg viewBox="0 0 360 130" className={className} role="img" aria-label="Terre et Mer Conciergerie">
      {/* Intertwined monogram */}
      <g transform="translate(180, 50)">
        {/* T */}
        <line x1="-24" y1="-22" x2="14" y2="-22" stroke={ink} strokeWidth="0.8" strokeLinecap="round" />
        <line x1="-5" y1="-22" x2="-5" y2="22" stroke={ink} strokeWidth="0.8" strokeLinecap="round" />
        {/* M intertwined with T base */}
        <path d="M -22 22 L -22 0 L -8 14 L 6 0 L 6 22" fill="none" stroke={accent} strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round" />
        {/* Decorative ampersand */}
        <text x="22" y="6" fontFamily="Fraunces, Georgia, serif" fontWeight="300" fontStyle="italic" fontSize="22" fill={accent}>&amp;</text>
        {/* Hairline frame dots */}
        <circle cx="-44" cy="0" r="1" fill={ink} opacity="0.4" />
        <circle cx="44" cy="0" r="1" fill={ink} opacity="0.4" />
      </g>
      {/* Wordmark with hairline rules */}
      <line x1="60" y1="92" x2="140" y2="92" stroke={accent} strokeWidth="0.4" />
      <line x1="220" y1="92" x2="300" y2="92" stroke={accent} strokeWidth="0.4" />
      <text x="180" y="96" textAnchor="middle" fontFamily="Fraunces, Georgia, serif" fontWeight="300" fontSize="13" fill={ink} letterSpacing="8">TERRE ET MER</text>
      <text x="180" y="116" textAnchor="middle" fontFamily="Outfit, sans-serif" fontWeight="500" fontSize="8" fill={ink} opacity="0.55" letterSpacing="5">- CONCIERGERIE PRIVÉE -</text>
    </svg>
  );
};

// ============================================================
// CONCEPT G, Cachet circulaire avec EST. année
// Style : maison ancestrale, parfumerie, cognac de prestige
// ============================================================
export const LogoG = ({ variant = "dark", className = "" }) => {
  const ink = variant === "dark" ? "#0B1A26" : "#FFFFFF";
  const accent = "#E08F5C";
  return (
    <svg viewBox="0 0 360 140" className={className} role="img" aria-label="Terre et Mer Conciergerie">
      <g transform="translate(180, 70)">
        {/* Outer circle */}
        <circle cx="0" cy="0" r="58" fill="none" stroke={ink} strokeWidth="0.6" />
        <circle cx="0" cy="0" r="54" fill="none" stroke={accent} strokeWidth="0.4" />
        {/* Top curved text */}
        <defs>
          <path id="topArc" d="M -50 -5 A 50 50 0 0 1 50 -5" fill="none" />
          <path id="bottomArc" d="M -45 18 A 45 45 0 0 0 45 18" fill="none" />
        </defs>
        <text fontFamily="Outfit, sans-serif" fontWeight="600" fontSize="7" fill={ink} letterSpacing="5">
          <textPath href="#topArc" startOffset="50%" textAnchor="middle">TERRE  ET  MER  CONCIERGERIE</textPath>
        </text>
        <text fontFamily="Outfit, sans-serif" fontWeight="500" fontSize="6" fill={accent} letterSpacing="6">
          <textPath href="#bottomArc" startOffset="50%" textAnchor="middle">BRETAGNE  ·  CÔTE  D'ÉMERAUDE</textPath>
        </text>
        {/* Center monogram T·M */}
        <text x="0" y="-2" textAnchor="middle" fontFamily="Fraunces, Georgia, serif" fontWeight="300" fontStyle="italic" fontSize="38" fill={ink}>T</text>
        <text x="14" y="-2" textAnchor="middle" fontFamily="Fraunces, Georgia, serif" fontWeight="300" fontStyle="italic" fontSize="38" fill={accent}>m</text>
        {/* EST line */}
        <line x1="-12" y1="6" x2="12" y2="6" stroke={accent} strokeWidth="0.6" />
        <text x="0" y="18" textAnchor="middle" fontFamily="Outfit, sans-serif" fontWeight="600" fontSize="6" fill={ink} opacity="0.8" letterSpacing="2">EST. 2024</text>
        {/* Side ornaments */}
        <text x="-58" y="2" fontFamily="serif" fontSize="10" fill={accent}>✦</text>
        <text x="52" y="2" fontFamily="serif" fontSize="10" fill={accent}>✦</text>
      </g>
    </svg>
  );
};

// ============================================================
// CONCEPT H, Hairline éditorial ultra-luxe (style Hermès)
// Style : maison historique, hairline elegance, presse haut de gamme
// ============================================================
export const LogoH = ({ variant = "dark", className = "" }) => {
  const ink = variant === "dark" ? "#0B1A26" : "#FFFFFF";
  const accent = "#E08F5C";
  return (
    <svg viewBox="0 0 420 130" className={className} role="img" aria-label="Terre et Mer Conciergerie">
      {/* Top hairline rule with center ornament */}
      <line x1="40" y1="32" x2="190" y2="32" stroke={ink} strokeWidth="0.4" />
      <text x="210" y="36" textAnchor="middle" fontFamily="serif" fontSize="10" fill={accent}>✦</text>
      <line x1="230" y1="32" x2="380" y2="32" stroke={ink} strokeWidth="0.4" />

      {/* Main wordmark, hairline ultra-thin */}
      <text x="210" y="78" textAnchor="middle" fontFamily="Fraunces, Georgia, serif" fontWeight="300" fontSize="40" fill={ink} letterSpacing="14">TERRE</text>
      <text x="210" y="78" textAnchor="middle" fontFamily="Fraunces, Georgia, serif" fontWeight="300" fontStyle="italic" fontSize="40" fill={accent} dy="0">    </text>

      {/* Italic 'et' under wordmark */}
      <text x="210" y="104" textAnchor="middle" fontFamily="Fraunces, Georgia, serif" fontWeight="300" fontStyle="italic" fontSize="22" fill={accent} letterSpacing="3">et Mer</text>

      {/* Bottom hairline rule + caption */}
      <line x1="40" y1="118" x2="160" y2="118" stroke={ink} strokeWidth="0.4" opacity="0.5" />
      <text x="210" y="122" textAnchor="middle" fontFamily="Outfit, sans-serif" fontWeight="500" fontSize="8" fill={ink} opacity="0.7" letterSpacing="9">M A I S O N  D E  C O N C I E R G E R I E</text>
      <line x1="260" y1="118" x2="380" y2="118" stroke={ink} strokeWidth="0.4" opacity="0.5" />
    </svg>
  );
};
